'use client';
import * as React from 'react';
import { useTranslations } from 'next-intl';
import { cn } from '@/lib/utils';
import { Icon } from '@/components/site/ui';

/* Native-feeling form controls. The control IS the whole pill — no nested
   wrapper border — so focus shows one clean ring (fixes the double-glow). */

const CONTROL =
  'h-11 w-full rounded-field border border-input bg-white text-sm text-foreground outline-none transition-[border-color,box-shadow] ' +
  'placeholder:text-neutral-400 focus-visible:border-medlux focus-visible:shadow-field-focus ' +
  'disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground ' +
  // An invalid field says so with its own border, not only with a banner elsewhere.
  'aria-[invalid=true]:border-destructive aria-[invalid=true]:focus-visible:border-destructive';

/** Eye button that flips a password field between dots and plain text.
 *  Private to Input: every `type="password"` gets one, so no screen has to
 *  remember to add the affordance (and none can render a different one). */
function PasswordRevealButton({
  revealed,
  onToggle,
  disabled,
}: {
  revealed: boolean;
  onToggle: () => void;
  disabled?: boolean;
}) {
  const t = useTranslations('common.actions');
  const label = revealed ? t('hidePassword') : t('showPassword');
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      aria-label={label}
      aria-pressed={revealed}
      title={label}
      className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:text-foreground focus-visible:shadow-focus focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
    >
      <Icon name={revealed ? 'view-off' : 'view'} size={18} />
    </button>
  );
}

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  icon?: string;
  trailing?: React.ReactNode;
  /** Styles the positioning wrapper created for icon/trailing adornments. */
  containerClassName?: string;
  /** Opt a `type="password"` field out of the built-in reveal eye. */
  revealToggle?: boolean;
  /** glass: a translucent rounded pill for a field on glass over a picture (the sign-in). */
  variant?: 'default' | 'glass';
}

/** Field on glass: translucent, borderless until focus, a light top edge (owner, 2026-11-03). */
export const GLASS_FIELD =
  'h-12 rounded-full border-transparent bg-white/60 shadow-[inset_0_1px_0_rgba(255,255,255,0.85)] backdrop-blur-sm placeholder:text-foreground/45 focus-visible:bg-white/85 aria-[invalid=true]:border-destructive';

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, containerClassName, icon, trailing, revealToggle = true, type, variant = 'default', ...props }, ref) => {
    const glass = variant === 'glass';
    const [revealed, setRevealed] = React.useState(false);
    const isPassword = type === 'password';
    const showReveal = isPassword && revealToggle;
    // The eye rewrites the rendered type; `type` stays 'password' in the caller's
    // markup so autoComplete/password-manager semantics never change.
    const renderedType = showReveal && revealed ? 'text' : type;
    const adornment = showReveal ? (
      <>
        {trailing}
        <PasswordRevealButton
          revealed={revealed}
          onToggle={() => setRevealed((v) => !v)}
          disabled={props.disabled}
        />
      </>
    ) : (
      trailing
    );
    const field = (
      <input
        ref={ref}
        // Suppress password-manager field decoration (the grey overlay box) by
        // default. Credential fields can opt back in with data-1p-ignore={undefined}.
        data-1p-ignore=""
        data-lpignore="true"
        type={renderedType}
        className={cn(
          CONTROL,
          glass && GLASS_FIELD,
          glass ? 'px-5' : 'px-3.5',
          icon && (glass ? 'pl-11' : 'pl-10'),
          adornment && (showReveal ? 'pr-11' : 'pr-10'),
          className,
        )}
        {...props}
      />
    );
    if (!icon && !adornment) return field;
    return (
      <div className={cn('relative', containerClassName)}>
        {icon && (
          <span className={cn('pointer-events-none absolute top-1/2 z-[1] -translate-y-1/2 text-muted-foreground', glass ? 'left-4' : 'left-3.5')}>
            <Icon name={icon} size={18} />
          </span>
        )}
        {field}
        {adornment && (
          <span className={cn('absolute top-1/2 z-[1] -translate-y-1/2 flex items-center gap-1', glass ? 'right-3' : 'right-2.5')}>
            {adornment}
          </span>
        )}
      </div>
    );
  },
);
Input.displayName = 'Input';

export const Textarea = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
    /** Start at `rows` and grow with the content instead of sitting at a fixed height.
     *  A form of six of these at `min-h-24` reads as a wall before a word is typed. */
    autoGrow?: boolean;
  }
>(({ className, autoGrow, ...props }, ref) => {
  const inner = React.useRef<HTMLTextAreaElement | null>(null);
  const fit = React.useCallback(() => {
    const el = inner.current;
    if (!el || !autoGrow) return;
    // Reset first: without it the box can only ever grow, never shrink on delete.
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [autoGrow]);
  // Keyed on the value so a box filled from outside (opening an editor on a saved record)
  // sizes itself too, not only while typing.
  React.useLayoutEffect(fit, [fit, props.value]);
  return (
    <textarea
      data-1p-ignore=""
      data-lpignore="true"
      {...props}
      ref={(node) => {
        inner.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
      }}
      onInput={(e) => {
        fit();
        props.onInput?.(e);
      }}
      className={cn(
        CONTROL,
        'px-3.5 py-2.5 leading-relaxed',
        autoGrow ? 'resize-none overflow-hidden' : 'min-h-24 resize-y',
        className,
      )}
    />
  );
});
Textarea.displayName = 'Textarea';

export function Label({ className, ...props }: React.LabelHTMLAttributes<HTMLLabelElement>) {
  return <label className={cn('text-sm font-medium text-foreground', className)} {...props} />;
}

/** Label + control + optional hint/error, vertically stacked. */
export function Field({
  label,
  hint,
  error,
  htmlFor,
  count,
  className,
  children,
}: {
  label?: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  htmlFor?: string;
  /** Character budget of the control, shown bottom-right once it has content. Pass the
   *  SAME max as the control's `maxLength` — and the API's — or the two disagree. */
  count?: { value: number; max: number };
  className?: string;
  children: React.ReactNode;
}) {
  const msgId = React.useId();
  const labelId = React.useId();
  const describedBy = error || hint ? msgId : undefined;
  // Wire the hint/error to the control so screen readers announce it and mark the
  // field invalid on error, and NAME the control from the visible label.
  //
  // `aria-labelledby` rather than htmlFor/id because a control here is often a wrapper
  // (CurrencyInput, DatePicker, a Radix Select) whose real input is nested: those spread the
  // aria attributes they are given, so this reaches the input the way `aria-describedby`
  // already does, while an `id` would land on the wrapper and name nothing. A control that
  // already carries its own name keeps it.
  const props = React.isValidElement(children)
    ? (children.props as Record<string, unknown>)
    : ({} as Record<string, unknown>);
  const alreadyNamed = Boolean(props['aria-label'] ?? props['aria-labelledby']);
  const control =
    (describedBy || (label && !alreadyNamed)) && React.isValidElement(children)
      ? React.cloneElement(children as React.ReactElement<Record<string, unknown>>, {
          ...(describedBy ? { 'aria-describedby': describedBy } : {}),
          ...(error ? { 'aria-invalid': true } : {}),
          ...(label && !alreadyNamed ? { 'aria-labelledby': labelId } : {}),
        })
      : children;
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label && (
        <Label id={labelId} htmlFor={htmlFor}>
          {label}
        </Label>
      )}
      {control}
      {(error || hint || count) && (
        <div className="flex items-start justify-between gap-3">
          {error ? (
            <p id={msgId} className="text-xs text-destructive">
              {error}
            </p>
          ) : hint ? (
            <p id={msgId} className="text-xs text-muted-foreground">
              {hint}
            </p>
          ) : (
            <span />
          )}
          {/* Only once there is something to count: a 0/400 on an untouched field is noise,
              and the number matters exactly when the cap is close. */}
          {count && count.value > 0 && (
            <span
              className={cn(
                'shrink-0 text-[0.6875rem] tabular-nums',
                count.value >= count.max ? 'text-destructive' : 'text-muted-foreground',
              )}
            >
              {count.value}/{count.max}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/** Inline checkbox + label row (native checkbox, brand-accent). */
export function CheckboxField({
  label,
  className,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & { label: React.ReactNode }) {
  return (
    <label
      className={cn('flex cursor-pointer items-center gap-2.5 text-sm text-foreground', className)}
    >
      <input
        type="checkbox"
        className="h-4 w-4 rounded border-input text-medlux accent-medlux focus-visible:shadow-focus"
        {...props}
      />
      {label}
    </label>
  );
}
